/**
 * Core Layer - Exceptions
 * Technical exceptions for error handling
 *
 * NOTE: These are technical error classes, not user-facing messages.
 * User-facing messages should come from translation files (messages/*.json)
 */

import { HTTP_STATUS } from "@/constants";
import { ApiErrorResponse } from "../types";

/**
 * Base class for all API related errors
 */
export class ApiError extends Error implements ApiErrorResponse {
  public readonly statusCode?: number;
  public readonly code?: string;
  success: false;
  response?: { message: string } | null | undefined;

  constructor(message: string, statusCode?: number, code?: string) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = statusCode;
    this.code = code;
    this.success = false;
    this.response = { message: message };

    // Fixes the prototype chain for ES5
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Error thrown when the network is unavailable
 */
export class NetworkError extends ApiError {
  constructor(message?: string) {
    super(message ?? "network_error");
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Error thrown when authentication fails
 */
export class AuthError extends ApiError {
  constructor(message?: string, statusCode = HTTP_STATUS.UNAUTHORIZED) {
    super(message ?? "auth_error", statusCode);
    Object.setPrototypeOf(this, AuthError.prototype);
  }
}

/**
 * Error thrown when a request is unauthorized
 */
export class ForbiddenError extends ApiError {
  constructor(message?: string, statusCode = HTTP_STATUS.FORBIDDEN) {
    super(message ?? "forbidden_error", statusCode);
    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

/**
 * Error thrown when a resource is not found
 */
export class NotFoundError extends ApiError {
  constructor(message?: string, statusCode = HTTP_STATUS.NOT_FOUND) {
    super(message ?? "not_found_error", statusCode);
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

/**
 * Error thrown when a validation error occurs
 */
export class ValidationError extends ApiError {
  public readonly errors?: Record<string, string[]>;

  constructor(
    message?: string,
    errors?: Record<string, string[]>,
    statusCode = HTTP_STATUS.UNPROCESSABLE_ENTITY
  ) {
    super(message ?? "validation_error", statusCode);
    this.errors = errors;
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * Error thrown when the server returns a 500 error
 */
export class ServerError extends ApiError {
  constructor(
    message?: string,
    statusCode = HTTP_STATUS.INTERNAL_SERVER_ERROR
  ) {
    super(message ?? "server_error", statusCode);
    Object.setPrototypeOf(this, ServerError.prototype);
  }
}

/**
 * Error thrown when a request times out
 */
export class TimeoutError extends ApiError {
  constructor(message?: string) {
    super(message ?? "timeout_error", HTTP_STATUS.REQUEST_TIMEOUT);
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}
